import { Asset } from 'types/Attachments';
import { MediaLibraryItem } from 'types/MediaLibrary';
import { pickAssetThumbnail } from 'app/modules/mediaLibraryPage/utils/pickAssetThumbnail';

/**
 * Bridges a new Media Library `Asset` into the `MediaLibraryItem` shape that the
 * composer's file-source `onChange` handlers consume.
 *
 * The item carries the authoritative `asset` object so that
 * `mediaLibraryItemToAssetCard` produces an asset-shape card
 * (`{ medium: null, asset }`) end-to-end. The remaining synthetic fields are a
 * best-effort fallback for any consumer that still reads the legacy item shape.
 */
export function assetToMediaLibraryItem(asset: Asset): MediaLibraryItem {
  const thumbnail = pickAssetThumbnail(asset) ?? asset.url ?? '';
  const width = asset.width ?? 0;
  const height = asset.height ?? 0;
  const thumb = { src: thumbnail, width, height };

  if (asset.type === 'video') {
    return {
      // The legacy item `id` is a numeric medium id that asset-shape consumers
      // never read — `asset`/`assetId` below carry the authoritative identity.
      id: 0,
      type: 'video',
      assetId: asset.id,
      asset,
      data: {
        src: asset.url ?? '',
        width,
        height,
        thumbnail: thumb,
      },
      meta: {
        duration: 0,
        is_available: asset.status === 'ready',
        is_error: asset.status === 'failed',
        is_processed: asset.status === 'ready',
      },
    };
  }

  if (asset.type === 'document') {
    return {
      id: asset.id,
      type: 'document',
      created_at: asset.created_at,
      data: {
        src: asset.url ?? '',
        thumbnail: thumb,
      },
      meta: {
        file_name: asset.file_name,
        file_size: asset.file_size,
      },
    };
  }

  return {
    id: 0,
    type: 'image',
    assetId: asset.id,
    asset,
    data: {
      src: asset.url ?? '',
      width,
      height,
      thumbnail: thumb,
      original: { src: asset.url ?? '', width, height },
    },
    meta: {
      usages: 0,
      is_animated_gif: asset.type === 'gif',
      is_panorama: false,
      is_spehrical: false,
    },
  };
}
